import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { Node, mergeAttributes } from '@tiptap/core';
import { useRef, useEffect, useState } from 'react';
import {
    Bold, Italic, Heading2, Heading3, List, ListOrdered,
    Link as LinkIcon, Image as ImageIcon, Undo, Redo, Minus,
    Palette, Highlighter, ChevronDown, Table2, Plus, Trash2,
    Info, AlertTriangle, CheckCircle, AlertOctagon
} from 'lucide-react';

const btnStyle = (active) => ({
    background: active ? 'var(--brand-bg)' : 'transparent',
    color: active ? 'var(--brand-text)' : 'var(--text-muted)',
    border: 'none',
    borderRadius: 5,
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'background 0.15s',
});

function ToolbarButton({ icon: Icon, active, onClick, title }) {
    return (
        <button type="button" onClick={onClick} title={title} style={btnStyle(active)}>
            <Icon size={16} />
        </button>
    );
}

const TEXT_COLORS = [
    { color: null, label: 'Výchozí' },
    { color: '#ef4444', label: 'Červená' },
    { color: '#f97316', label: 'Oranžová' },
    { color: '#eab308', label: 'Žlutá' },
    { color: '#22c55e', label: 'Zelená' },
    { color: '#3b82f6', label: 'Modrá' },
    { color: '#8b5cf6', label: 'Fialová' },
    { color: '#ec4899', label: 'Růžová' },
];

const HIGHLIGHT_COLORS = [
    { color: null, label: 'Žádné' },
    { color: '#fef08a', label: 'Žlutá' },
    { color: '#bbf7d0', label: 'Zelená' },
    { color: '#bfdbfe', label: 'Modrá' },
    { color: '#fecaca', label: 'Červená' },
    { color: '#e9d5ff', label: 'Fialová' },
    { color: '#fed7aa', label: 'Oranžová' },
];

function ColorDropdown({ icon: Icon, colors, activeColor, onSelect, title }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                title={title}
                style={{
                    ...btnStyle(!!activeColor),
                    width: 40,
                    gap: 2,
                }}
            >
                <Icon size={15} />
                <ChevronDown size={10} />
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, zIndex: 50,
                    background: 'var(--surface-raised)', border: '1px solid var(--border-default)',
                    borderRadius: 6, padding: 6, display: 'flex', gap: 4, flexWrap: 'wrap', width: 160,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}>
                    {colors.map((c, i) => (
                        <button
                            key={i}
                            type="button"
                            onClick={() => { onSelect(c.color); setOpen(false); }}
                            title={c.label}
                            style={{
                                width: 24, height: 24, borderRadius: 5,
                                background: c.color || 'var(--surface-base)',
                                border: `2px solid ${activeColor === c.color ? 'var(--brand)' : 'var(--border-default)'}`,
                                cursor: 'pointer',
                                position: 'relative',
                            }}
                        >
                            {!c.color && (
                                <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: 'var(--text-muted)' }}>✕</span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ── Callout block extension ──
const Callout = Node.create({
    name: 'callout',
    group: 'block',
    content: 'block+',
    defining: true,
    addAttributes() {
        return {
            type: { default: 'info', parseHTML: el => el.getAttribute('data-callout') || 'info',
                renderHTML: attrs => ({ 'data-callout': attrs.type }) },
        };
    },
    parseHTML() { return [{ tag: 'div[data-callout]' }]; },
    renderHTML({ HTMLAttributes }) {
        return ['div', mergeAttributes(HTMLAttributes), 0];
    },
});

const CALLOUT_TYPES = [
    { type: 'info', label: 'Info', Icon: Info, color: '#3b82f6' },
    { type: 'warning', label: 'Upozornění', Icon: AlertTriangle, color: '#f59e0b' },
    { type: 'success', label: 'Správně', Icon: CheckCircle, color: '#22c55e' },
    { type: 'danger', label: 'Nebezpečí', Icon: AlertOctagon, color: '#ef4444' },
];

function CalloutDropdown({ editor }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setOpen(!open)} title="Callout blok"
                style={{ ...btnStyle(editor.isActive('callout')), width: 40, gap: 2 }}>
                <Info size={15} /><ChevronDown size={10} />
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, zIndex: 50,
                    background: 'var(--surface-raised)', border: '1px solid var(--border-default)',
                    borderRadius: 6, padding: 4, minWidth: 160, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}>
                    {CALLOUT_TYPES.map(c => (
                        <button key={c.type} type="button"
                            onClick={() => {
                                if (editor.isActive('callout', { type: c.type })) {
                                    editor.chain().focus().lift('callout').run();
                                } else {
                                    editor.chain().focus().wrapIn('callout', { type: c.type }).run();
                                }
                                setOpen(false);
                            }}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                                padding: '6px 10px', fontSize: 13, borderRadius: 5,
                                background: 'transparent', border: 'none', cursor: 'pointer',
                                color: 'var(--text-primary)',
                            }}
                            onMouseEnter={e => e.target.style.background = 'var(--surface-elevated)'}
                            onMouseLeave={e => e.target.style.background = 'transparent'}>
                            <c.Icon size={14} style={{ color: c.color, flexShrink: 0 }} />
                            {c.label}
                        </button>
                    ))}
                    {editor.isActive('callout') && (
                        <>
                            <div style={{ height: 1, background: 'var(--border-default)', margin: '4px 0' }} />
                            <button type="button"
                                onClick={() => { editor.chain().focus().lift('callout').run(); setOpen(false); }}
                                style={{
                                    display: 'block', width: '100%', textAlign: 'left',
                                    padding: '6px 10px', fontSize: 13, borderRadius: 5,
                                    background: 'transparent', border: 'none', cursor: 'pointer',
                                    color: '#ef4444',
                                }}
                                onMouseEnter={e => e.target.style.background = 'var(--surface-elevated)'}
                                onMouseLeave={e => e.target.style.background = 'transparent'}>
                                Odebrat callout
                            </button>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

function TableDropdown({ editor }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const inTable = editor.isActive('table');

    useEffect(() => {
        if (!open) return;
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const items = inTable ? [
        { label: 'Přidat řádek', action: () => editor.chain().focus().addRowAfter().run() },
        { label: 'Přidat sloupec', action: () => editor.chain().focus().addColumnAfter().run() },
        { label: 'Smazat řádek', action: () => editor.chain().focus().deleteRow().run(), danger: true },
        { label: 'Smazat sloupec', action: () => editor.chain().focus().deleteColumn().run(), danger: true },
        { label: 'Smazat tabulku', action: () => editor.chain().focus().deleteTable().run(), danger: true },
    ] : [
        { label: 'Vložit tabulku 3×3', action: () => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
        { label: 'Vložit tabulku 2×2', action: () => editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run() },
    ];

    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button type="button" onClick={() => setOpen(!open)} title="Tabulka"
                style={{ ...btnStyle(inTable), width: 40, gap: 2 }}>
                <Table2 size={15} /><ChevronDown size={10} />
            </button>
            {open && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, zIndex: 50,
                    background: 'var(--surface-raised)', border: '1px solid var(--border-default)',
                    borderRadius: 6, padding: 4, minWidth: 170, boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}>
                    {items.map((item, i) => (
                        <button key={i} type="button"
                            onClick={() => { item.action(); setOpen(false); }}
                            style={{
                                display: 'block', width: '100%', textAlign: 'left',
                                padding: '6px 10px', fontSize: 13, borderRadius: 5,
                                background: 'transparent', border: 'none', cursor: 'pointer',
                                color: item.danger ? '#ef4444' : 'var(--text-primary)',
                            }}
                            onMouseEnter={e => e.target.style.background = 'var(--surface-elevated)'}
                            onMouseLeave={e => e.target.style.background = 'transparent'}>
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

export default function TipTapEditor({ value, onChange, skoleniId, placeholder = 'Sem napište obsah...', nahratObrazek = null }) {
    const fileInputRef = useRef(null);
    const isInternalChange = useRef(false);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [2, 3] },
            }),
            Image.configure({ inline: false, allowBase64: false }),
            Link.configure({ openOnClick: false }),
            Placeholder.configure({ placeholder }),
            TextStyle,
            Color,
            Highlight.configure({ multicolor: true }),
            Table.configure({ resizable: false }),
            TableRow,
            TableHeader,
            TableCell,
            Callout,
        ],
        content: value || '',
        onUpdate: ({ editor }) => {
            isInternalChange.current = true;
            onChange(editor.getHTML());
        },
    });

    // Sync external value changes (e.g. loading from server) — skip if change came from editor
    useEffect(() => {
        if (isInternalChange.current) {
            isInternalChange.current = false;
            return;
        }
        if (editor && value !== undefined && editor.getHTML() !== value) {
            editor.commands.setContent(value || '', false);
        }
    }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleImageUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !editor) return;

        // Mimo školení (Znalosti) si nahrání řeší volající — vrátí URL obrázku
        if (nahratObrazek) {
            try {
                const url = await nahratObrazek(file);
                if (url) editor.chain().focus().setImage({ src: url }).run();
            } catch (err) {
                console.error('Image upload failed', err);
            }
            e.target.value = '';
            return;
        }

        const token = document.querySelector('meta[name="csrf-token"]')?.content;
        const fd = new FormData();
        fd.append('image', file);

        try {
            const resp = await fetch(`/api/skoleni/${skoleniId}/upload-image`, {
                method: 'POST',
                headers: { 'X-CSRF-TOKEN': token },
                body: fd,
            });
            const data = await resp.json();
            if (data.url) {
                editor.chain().focus().setImage({ src: data.url }).run();
            }
        } catch (err) {
            console.error('Image upload failed', err);
        }

        e.target.value = '';
    };

    const addLink = () => {
        if (!editor) return;
        const url = window.prompt('Zadejte URL odkazu:');
        if (url) {
            editor.chain().focus().setLink({ href: url }).run();
        }
    };

    if (!editor) return null;

    const currentColor = editor.getAttributes('textStyle').color || null;
    const currentHighlight = editor.getAttributes('highlight').color || null;

    return (
        <div style={{ border: '1px solid var(--border-default)', borderRadius: 6, overflow: 'hidden' }}>
            {/* Toolbar */}
            <div
                className="flex flex-wrap gap-0.5 px-2 py-1.5"
                style={{ borderBottom: '1px solid var(--border-default)', background: 'var(--surface-elevated)' }}
            >
                <ToolbarButton icon={Bold} active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} title="Tučné" />
                <ToolbarButton icon={Italic} active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} title="Kurzíva" />
                <div style={{ width: 1, height: 20, background: 'var(--border-default)', margin: '6px 4px' }} />
                <ToolbarButton icon={Heading2} active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Nadpis 2" />
                <ToolbarButton icon={Heading3} active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Nadpis 3" />
                <div style={{ width: 1, height: 20, background: 'var(--border-default)', margin: '6px 4px' }} />
                <ColorDropdown
                    icon={Palette}
                    colors={TEXT_COLORS}
                    activeColor={currentColor}
                    onSelect={(c) => c ? editor.chain().focus().setColor(c).run() : editor.chain().focus().unsetColor().run()}
                    title="Barva textu"
                />
                <ColorDropdown
                    icon={Highlighter}
                    colors={HIGHLIGHT_COLORS}
                    activeColor={currentHighlight}
                    onSelect={(c) => c ? editor.chain().focus().toggleHighlight({ color: c }).run() : editor.chain().focus().unsetHighlight().run()}
                    title="Zvýraznění"
                />
                <div style={{ width: 1, height: 20, background: 'var(--border-default)', margin: '6px 4px' }} />
                <ToolbarButton icon={List} active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Odrážky" />
                <ToolbarButton icon={ListOrdered} active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Číslovaný seznam" />
                <div style={{ width: 1, height: 20, background: 'var(--border-default)', margin: '6px 4px' }} />
                <ToolbarButton icon={LinkIcon} active={editor.isActive('link')} onClick={addLink} title="Odkaz" />
                <ToolbarButton icon={ImageIcon} active={false} onClick={() => fileInputRef.current?.click()} title="Obrázek" />
                <ToolbarButton icon={Minus} active={false} onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Oddělovač" />
                <TableDropdown editor={editor} />
                <CalloutDropdown editor={editor} />
                <div style={{ width: 1, height: 20, background: 'var(--border-default)', margin: '6px 4px' }} />
                <ToolbarButton icon={Undo} active={false} onClick={() => editor.chain().focus().undo().run()} title="Zpět" />
                <ToolbarButton icon={Redo} active={false} onClick={() => editor.chain().focus().redo().run()} title="Vpřed" />
            </div>

            {/* Editor */}
            <EditorContent
                editor={editor}
                className="tiptap-content"
                style={{
                    minHeight: 200,
                    padding: '12px 16px',
                    color: 'var(--text-primary)',
                    background: 'var(--surface-base)',
                    fontSize: 14,
                    lineHeight: 1.7,
                }}
            />

            {/* Hidden file input */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
            />
        </div>
    );
}
